import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { isSearchEnabled } from 'lib/search/searchUtils'
import HeadData from 'components/Common/HeadData'
import { LUXURY_ESCAPES } from 'constants/brands'
import { ANYWHERE_PLACE_ID, ANYWHERE_NAME, TYPE_PAGE_LIMIT } from 'constants/search'
import config from 'constants/config'
import { fetchListBanners } from 'actions/ContentActions'
import { fetchHotelPopularHolidayTypes } from 'actions/DestinationAlertsActions'
import { BANNER_LOCATION_HOTELS_AND_RESORTS } from 'constants/bannerLocations'
import isAnyFilterApplied from 'lib/offer/filtersUtils'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import styled from 'styled-components'
import { rem } from 'polished'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { OFFER_TYPE_VILLA } from 'constants/offer'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelPageSearchHero from '../Hotels/HotelPageSearchHero'
import useHotelAnalytics from '../Hotels/hooks/useHotelAnalytics'
import { usePlaceByName } from 'hooks/usePlace'
import HotelOfferCarousel from 'home/components/OfferCarousels/HotelOfferCarousel'
import { PlacesByPlaceId, getPlaceByRegionCode } from 'constants/places'
import { GlobalSearchTrackingProvider } from 'contexts/GlobalSearch/GlobalSearchTracking'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import OfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'
import AnywhereFilterTabs from 'components/Pages/HotelSearchPage/AnywhereFilterTabs'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'

export const HotelSearchContainerPaneContent = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

const FilterCarouselSection = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

interface MappedStateProps {
  windowSearch: string
  trendingDestinations: Array<App.TrendingDestination>
  filters: App.OfferListFilters
  regionCode: string
  geoStateName?: string
  isSearchEnabled: boolean
}

interface MappedDispatchProps {
  fetchListBanners: Utils.ActionDispatcher<typeof fetchListBanners>
}

const villaViewAllUrl = `/search/homes-and-villas?destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&adults=2&children=none`
const rentalsAllTypesViewAllUrl = `/search/homes-and-villas?destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&adults=2&children=none`
const rentalsFilterCarouselDestinations: Array<{ label: string, value: string }> = [
  { label: 'Indonesia', value: 'le_43ec517d68b6edd3015b3edc9a11367b' },
  { label: 'Thailand', value: 'le_8f85517967795eeef66c225f7883bdcb' },
  { label: 'Gold Coast', value: 'le_bac732346917b0a1231354ef55faf00a' },
]
const rentalsFilterDefaultDestinationId = rentalsFilterCarouselDestinations[0]?.value

function RentalsPage({
  filters,
  windowSearch,
  regionCode,
  isSearchEnabled,
  fetchListBanners,
  geoStateName,
}: MappedStateProps & MappedDispatchProps) {
  const intl = useIntl()
  const isManualSortingEnabled = isManualSortingEnabledFor('hotels')
  useEffect(() => {
    fetchListBanners(BANNER_LOCATION_HOTELS_AND_RESORTS)
  }, [fetchListBanners])

  const dispatch = useAppDispatch()
  const hasFilters = isAnyFilterApplied(filters)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext()

  const {
    onSearchEvent,
    onHomesVillasHotelListEvent,
    onWeekendHotelListEvent,
  } = useHotelAnalytics()

  const headTitle = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: 'dmGlDPgtye',
        defaultMessage: 'Luxury Homes & Villas for Your Next Escape',
        description: 'Page title for luxury rentals page',
      })
    : intl.formatMessage({
        id: 'jiKA8VqESK',
        defaultMessage: 'Homes & Villas',
        description: 'Page title for rentals page (non-luxury brand)',
      })
  const description = config.BRAND === LUXURY_ESCAPES
    ? intl.formatMessage({
        id: 'mz3rzCSmYc',
        defaultMessage: 'Book entire homes and apartments designed for togetherness. Self\u2010cater, spread out, and make your stay feel like home.',
        description: 'Meta description for luxury rentals page',
      })
    : undefined

  const finalFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !hasFilters && !filters.sortBy) {
      return {
        offerTypes: [OFFER_TYPE_VILLA],
        sortBy: 'vertical-hotel',
        limit: TYPE_PAGE_LIMIT,
      }
    }

    return {
      ...filters,
      offerTypes: [OFFER_TYPE_VILLA],
      frontPageVisibleOnly: true,
      limit: TYPE_PAGE_LIMIT,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters, hasFilters, isManualSortingEnabled])
  const geoStatePlace = usePlaceByName(geoStateName)

  const userPlace = useMemo(() => {
    // if regionCode is AU, use geoStatePlace if it exists
    if (regionCode === 'AU' && geoStatePlace) {
      return geoStatePlace
    }
    return getPlaceByRegionCode(regionCode)
  }, [geoStatePlace, regionCode])

  const userPlaceViewURL = `/search/homes-and-villas?adults=2&children=none&destinationId=${userPlace.id}&destinationName=${userPlace.name}`
  const rentalsFilterCarouselFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_VILLA],
    frontPageVisibleOnly: true,
    limit: MAX_CAROUSEL_ITEM_COUNT,
    sortBy: 'homepage',
    destinationId: rentalsFilterDefaultDestinationId,
  }), [])
  const rentalsAllTypesCarouselFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_VILLA],
    frontPageVisibleOnly: true,
    limit: MAX_CAROUSEL_ITEM_COUNT,
    sortBy: 'homepage',
  }), [])
  const rentalsAllTypesActiveFilters = useMemo<App.OfferListFilters>(() => ({
    ...rentalsAllTypesCarouselFilters,
    holidayTypes: filters.holidayTypes,
    amenities: filters.amenities,
    sortBy: filters.sortBy ?? rentalsAllTypesCarouselFilters.sortBy,
  }), [filters.amenities, filters.holidayTypes, filters.sortBy, rentalsAllTypesCarouselFilters])
  const buildRentalsFilterAllLink = useMemo(() => {
    return (selectedFilter?: string) => {
      if (!selectedFilter) {
        return villaViewAllUrl
      }
      const [pathname, queryString = ''] = villaViewAllUrl.split('?')
      const params = new URLSearchParams(queryString)
      const selectedPlace = PlacesByPlaceId[selectedFilter]
      params.set('destinationId', selectedFilter)
      if (selectedPlace?.name) {
        params.set('destinationName', selectedPlace.name)
      }
      const nextQuery = params.toString()
      return nextQuery ? `${pathname}?${nextQuery}` : pathname
    }
  }, [])
  const rentalsHeroTitle = intl.formatMessage({
    id: 'JkPocMGiYQ',
    defaultMessage: 'Homes & Villas',
    description: 'Hero title for rentals page',
  })

  const rentalsHeroSubtitle = intl.formatMessage({
    id: '/fHUnnuznn',
    defaultMessage: 'Choose from hundreds of handpicked stays',
    description: 'Hero subtitle for rentals page',
  })

  const rentalsHeroCarouselTitle = intl.formatMessage(
    {
      id: 'pIQ5PY/DIy',
      defaultMessage: 'Discover <i>weekend getaways</i>',
      description: 'Title for weekend getaways carousel on rentals page with italic styling',
    },
    { i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i> },
  )

  const rentalsFilterCarouselTitle = intl.formatMessage(
    {
      id: 'XvR8lfAyBS',
      defaultMessage: 'Plan your <i>tropical</i> escape',
      description: 'Title for tropical escape carousel on rentals page with italic styling',
    },
    { i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i> },
  )

  const rentalsAllTypesCarouselTitle = intl.formatMessage({
    id: 'FupJeaygcP',
    defaultMessage: 'Find your perfect place to stay',
    description: 'Title for all types carousel on rentals page',
  })

  const flashOfferList = useOfferList(finalFilters)
  useOfferListFilters(rentalsAllTypesActiveFilters)
  useOfferListFilters(rentalsFilterCarouselFilters)

  useEffect(() => {
    dispatch(fetchHotelPopularHolidayTypes())
  }, [dispatch])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <div>
      <HeadData title={`${headTitle} - ${config.title} ${regionCode}`} description={description} />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        {(isSearchEnabled &&
          <GlobalSearchTrackingProvider onEvent={onSearchEvent}>
            <HotelPageSearchHero
              floatingSearchBar={!flashOfferList.fetching && !flashOfferList.offerIds.length}
              useInlineSearchBar
              useCompactMobileExpandedSpacing
              searchBasePath="/search/homes-and-villas"
              heroTitle={rentalsHeroTitle}
              heroSubtitle={rentalsHeroSubtitle}
              heroImageId="rentals-landing-page-canada"
              useDropShadow
            />
          </GlobalSearchTrackingProvider>)}
        <OfferListEventsProvider onListEvent={onWeekendHotelListEvent}>
          <HotelOfferCarousel
            title={rentalsHeroCarouselTitle}
            offerTypes={[OFFER_TYPE_VILLA]}
            viewAllUrl={userPlaceViewURL}
            destinationId={userPlace.id}
            hideFilters
          />
        </OfferListEventsProvider>
        <FilterCarouselSection gap={24}>
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
                <Heading variant="heading2">{rentalsAllTypesCarouselTitle}</Heading>
                <TextButton kind="tertiary" size="medium" to={rentalsAllTypesViewAllUrl}>
                  {intl.formatMessage({
                    id: '6E/1IpY+KS',
                    defaultMessage: 'View all',
                    description: 'Button to view all rental offers',
                  })}
                </TextButton>
              </Group>
              <AnywhereFilterTabs
                filters={rentalsAllTypesActiveFilters}
                search={windowSearch}
                excludedOptions={['Homes & Villas']}
              />
            </VerticalSpacer>
          </LayoutContainer>
          <OfferListCarousel
            filters={rentalsAllTypesActiveFilters}
            tileStyle="card-default"
            tileSize="medium"
            eagerLoadImages
          >
            <OfferCarouselLookingForSomethingElse to={rentalsAllTypesViewAllUrl} />
          </OfferListCarousel>
        </FilterCarouselSection>

      </GlobalSearchContextProvider>
      <OfferListEventsProvider onListEvent={onHomesVillasHotelListEvent}>
        <OfferCardCarouselSection
          filters={rentalsFilterCarouselFilters}
          title={rentalsFilterCarouselTitle}
          allLink={villaViewAllUrl}
          buildAllLink={buildRentalsFilterAllLink}
          additionalFilters="destinations"
          additionalFilterOptions={rentalsFilterCarouselDestinations}
          defaultFilter={rentalsFilterDefaultDestinationId}
          tileStyle="card-default"
          tileSize="medium"
          data-testid="rentals-filter-carousel"
        >
          <OfferCarouselLookingForSomethingElse to={villaViewAllUrl} />
        </OfferCardCarouselSection>
      </OfferListEventsProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  windowSearch: state.router.location.search,
  trendingDestinations: state.destination.trendingDestinations,
  filters: getUrlOfferListFilters(state),
  regionCode: state.geo.currentRegionCode,
  geoStateName: state.geo.geoStateName,
  isSearchEnabled: isSearchEnabled(),
})

const mapDispatchToProps: MappedDispatchProps = {
  fetchListBanners,
}

export default connect(mapStateToProps, mapDispatchToProps)(RentalsPage)
